<template>
  <validation-observer
    v-if="isRenderAfterFullfilled"
    v-slot="observer"
    class="subform-wrapper"
    slim
  >
    <form-section
      id="about"
      v-bind="observer"
      :validated="formObserver.validated && observer.failed"
    >
      <form-row :label="`About your ${productName}`">
        <form-section-question
          v-if="isShownVehicleDetails"
          slot="question"
        >
          Based on the vehicle {{ stepData.vehicleRegistrationNumber ? 'registration' : 'selection' }}, we think this {{ productName }}:
        </form-section-question>
      </form-row>

      <!-- VEHICLE DETAILS -->
      <form-row
        v-if="isShownVehicleDetails"
        gap="48"
      >
        <box>
          <subform-preview
            :preview-list="computedPreviewList"
            :icon="vehicleIcon"
          />
          <a
            slot="right-action"
            href="#"
            class="link-box"
            data-test="change_details_btn"
            @click.prevent="isShownVehicleDetails = false"
          >
            <span>Change these details</span>
            <v-icon color="primary">mdi-pencil</v-icon>
          </a>
        </box>
      </form-row>

      <!-- QUESTIONS -->
      <form-row
        v-else
        gap="48"
      >
        <form-section-highlight>
          <component
            :is="question.questionComponent"
            v-for="question in filteredQuestions"
            :key="question.questionId"
            class="subform-style"
            :class="{ 'no-validity': productName === 'motorhome' }"
            :product-name="productName"
            step-id="vehicle"
            :question-id="question.questionId"
            v-bind="question.params || {}"
            :form-observer="formObserver"
            :form-data="formData"
            :step-data="stepData"
            @input="onInput"
          />
        </form-section-highlight>
      </form-row>

      <question-base-radio-list
        v-if="isABVehicleTrackerSeparate"
        product-name="car"
        step-id="vehicle"
        class="subform-style no-validity"
        :question-id="splitTestQ.questionId"
        v-bind="splitTestQ.params || {}"
        :form-observer="formObserver"
        :form-data="formData"
        :step-data="stepData"
        @input="onInput"
      />
    </form-section>
  </validation-observer>
</template>

<script>
import QuestionBase from './Base';
import SubformPreview from '@/components/SubformController/SubformPreview';
import {
  inject,
} from '@vue/composition-api';
import { pick } from 'lodash';

export default {
  name: 'question-about',
  extends: QuestionBase,
  components: {
    SubformPreview,
  },
  props: {
    questions: {
      type: Array,
      required: true,
    },
  },
  inject: [
    'productName',
  ],
  data () {
    return {
      isShownVehicleDetails: false,
    };
  },
  created () {
    this.isShownVehicleDetails = !this.isSomeFieldMissing;
  },
  computed: {
    isABVehicleTrackerSeparate () {
      return this.isVehicleTrackerQuestionSeparate && this.productName === 'car';
    },
    isSomeFieldMissing () {
      return Object.values(pick(this.stepData, this.fields)).some(item => item === null);
    },
    filteredQuestions () {
      return this.questions.filter(question => {
        return !(this.isVehicleTrackerQuestionSeparate && this.productName === 'car' && question.questionId === 'vehicle-tracking-device');
      });
    },
    fields () {
      return this.filteredQuestions
        .map(question => question.params.customFields.field).filter(Boolean);
    },
    vehicleIcon () {
      switch (this.productName) {
        case 'van':
          return 'mdi-van-utility';
        case 'motorhome':
          return 'mdi-rv-truck';
        case 'car':
        default:
          return 'mdi-car';
      }
    },
    computedPreviewList () {
      const mappings = {
        vehicleSide: {
          prepend: 'Is',
          formatValue: (value) => `${value ? 'left' : 'right'} hand drive`,
          append: (value) => value === false ? '(UK standard)' : null,
        },
        seatsOfVehicle: {
          prepend: 'Has',
          formatValue: (value) => `${value} seats`,
        },
        vehicleAlarm: {
          prepend: 'Has',
          formatValue: (value) => {
            if (value === 'N') return 'no alarm / immobiliser';
            const alarmType = this.constants.VEHICLE_ALARM_IMMOBILISER[value];
            return `${value === 'O' ? 'an' : 'a'} ${alarmType} alarm / immobiliser`;
          },
        },
        vehicleTrackingDevice: {
          prepend: 'Has',
          formatValue: (value) => `${value ? '' : 'no '}tracker device fitted`,
          condition: () => !this.isVehicleTrackerQuestionSeparate,
        },
        vehicleGreyOrImport: {
          prepend: 'Is',
          formatValue: (value) => {
            if (this.productName === 'van') {
              return value === false ? 'not imported' : 'imported';
            }
            return value === 'N'
              ? 'not imported'
              : `imported - ${this.constants.VEHICLE_GREY_OR_IMPORT[value]}`;
          },
        },
      };

      return Object.entries(mappings).reduce((acc, [field, config]) => {
        if (this.questionData[field] !== undefined && (!config.condition || config.condition())) {
          acc.push({
            prepend: config.prepend,
            value: config.formatValue(this.questionData[field]),
            append: config.append ? config.append(this.questionData[field]) : null,
          });
        }
        return acc;
      }, []);
    },
  },
  watch: {
    isRenderAfterFullfilled: {
      async handler () {
        // wait for questions to set values to sessionData so isSomeFieldMissing return correct result
        await this.$nextTick();
        this.isShownVehicleDetails = !this.isSomeFieldMissing;
      },
    },
  },
  methods: {
    onInput (event) {
      this.$emit('input', event);
    },
  },
  setup () {
    const splitTestQ = {
      questionId: 'vehicle-tracking-device',
      params: {
        customFields: {
          field: 'vehicleTrackingDevice',
        },
        subtitle: 'Does it have a tracking device?',
        row: true,
        optionsConfig: {
          options: {
            true: 'Yes',
            false: 'No',
            DK: "I don't know",
          },
        },
      },
    };

    const {
      isVehicleTrackerQuestionSeparate,
      isCarHelpTextR1visible,
    } = inject('abTastyControllerUI');

    return {
      splitTestQ,
      isVehicleTrackerQuestionSeparate,
      isCarHelpTextR1visible,
    };
  },
};
</script>

<style lang="scss" src='@/assets/scss/subform/subform.scss'></style>
